import * as React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import useFormik from 'hooks/useFormik';
import { CustomFormControl } from 'components/Form/FormControl';
import ActionRow from 'components/ActionRow';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';

import { validationSchema } from './validationSchema';
import styles from './styles.module.scss';
import { formatAsPhone } from '../../../../functions/format/formatAsPhone';

export type IDriverProfile = {
  id: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  phone: string;
  email: string;
};

type InputProps = {
  driver: IDriverProfile;
  onSubmit: (driverProfile: IDriverProfile) => void;
};

export const DriverProfileForm = ({ driver, onSubmit }: InputProps) => {
  const { t } = useTranslation();

  const { getFieldProps, getFieldErrorProp, handleSubmit, isValid } = useFormik(
    {
      initialValues: {
        ...driver,
        phone: formatAsPhone(driver.phone),
      },
      validationSchema,
      enableReinitialize: true,
      onSubmit(value: IDriverProfile) {
        onSubmit({
          ...value,
          firstName: value.firstName.trim(),
          lastName: value.lastName.trim(),
          email: value.email.trim().toLowerCase(),
          phone: `+1${value.phone.replace(/\D/g, '')}`.trim(),
        });
      },
    }
  );

  const handlerChangePhoneNumber = (e: any) => {
    getFieldProps('phone').onChange(
      formatAsPhone(e.target.value.toString(), getFieldProps('phone').value)
    );
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={clsx(styles.row, styles.mb)}>
        <CustomFormControl
          error={getFieldErrorProp('firstName').error}
          label={t('DriverProfileForm.label.firstName')}
          required={true}
          inputProps={getFieldProps('firstName')}
        />
        <CustomFormControl
          error={getFieldErrorProp('lastName').error}
          label={t('DriverProfileForm.label.lastName')}
          required={true}
          inputProps={getFieldProps('lastName')}
        />
      </div>
      <div className={styles.row}>
        <CustomFormControl
          error={getFieldErrorProp('employeeId').error}
          label={t('DriverProfileForm.label.employeeId')}
          required={true}
          inputProps={getFieldProps('employeeId')}
        />
        <CustomFormControl
          error={getFieldErrorProp('phone').error}
          label={t('DriverProfileForm.label.phone')}
          required={true}
          inputProps={{
            ...getFieldProps('phone'),
            onChange: handlerChangePhoneNumber,
          }}
        />
      </div>
      <div>
        <CustomFormControl
          error={getFieldErrorProp('email').error}
          label={t('DriverProfileForm.label.email')}
          required={false}
          inputProps={getFieldProps('email')}
        />
      </div>
      <ActionRow style="right" marginTop={false} className={styles.actions}>
        <ButtonWithArrowIcon type="submit" disabled={!isValid}>
          {t('DriverProfileForm.btn.save')}
        </ButtonWithArrowIcon>
      </ActionRow>
    </form>
  );
};
